import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AssessmentService } from '../services/AssessmentService';
import { TeacherService } from '../services/TeacherService';
import { AssignmentService } from '../services/AssignmentService';
import { GradingService } from '../services/GradingService';
import { GradingEngine } from '../services/GradingEngine';
import { DiagnosticEngine } from '../services/DiagnosticEngine';
import { ReportGenerator } from '../services/ReportGenerator';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Heatmap from './Heatmap';
import TeachingPriorityPanel from './TeachingPriorityPanel';
import PerformanceDistribution from './PerformanceDistribution';
import LearningObjectiveAnalytics from './LearningObjectiveAnalytics';
import CohortComparison from './CohortComparison';

const RESULT_STATUS = Object.freeze({
  ORIGINAL: 'ORIGINAL',
  TEACHER_REVIEWED: 'TEACHER_REVIEWED',
  REVIEWED_PDF: 'REVIEWED_PDF',
  APPROVED: 'APPROVED',
  RELEASED: 'RELEASED'
});

const RESULT_VISIBILITY = Object.freeze({
  HIDDEN: 'HIDDEN',
  STUDENT: 'STUDENT'
});

/**
 * React must never receive a plain object as a child.
 * This helper converts any nested/object answer into a safe display string.
 */
const toSafeDisplayValue = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map(item => toSafeDisplayValue(item, ''))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'object') {
    const preferredKeys = [
      'studentAnswer',
      'answer',
      'value',
      'text',
      'response',
      'correctAnswer'
    ];

    for (const key of preferredKeys) {
      if (
        Object.prototype.hasOwnProperty.call(value, key) &&
        value[key] !== null &&
        value[key] !== undefined
      ) {
        return toSafeDisplayValue(value[key], fallback);
      }
    }

    return Object.entries(value)
      .map(([key, item]) => {
        const safeItem = toSafeDisplayValue(item, '');
        return safeItem ? `${key}: ${safeItem}` : key;
      })
      .join(', ');
  }

  return String(value);
};

const safeClone = value => {
  if (value === undefined || value === null) return value;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch (error) {
      // Fallback
    }
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const normalizeQuestionResults = submission => {
  if (!submission) return [];

  const results = submission.results;
  const answers = submission.answers;

  if (Array.isArray(results)) {
    return results.map((item, index) => {
      const questionId = toSafeDisplayValue(
        item?.questionId || item?.id || `Q${index + 1}`
      );

      const answerValue =
        item?.studentAnswer ??
        item?.answer ??
        item?.response ??
        answers?.[questionId] ??
        '';

      const correctValue =
        item?.correctAnswer ??
        item?.modelAnswer ??
        item?.expectedAnswer ??
        '';

      return {
        questionId,
        studentAnswer: toSafeDisplayValue(answerValue, ''),
        correctAnswer: toSafeDisplayValue(correctValue, ''),
        isCorrect: Boolean(item?.isCorrect ?? item?.correct ?? false)
      };
    });
  }

  if (results && typeof results === 'object' && !Array.isArray(results)) {
    return Object.entries(results).map(([questionId, item]) => {
      const answerValue =
        item && typeof item === 'object'
          ? (item.studentAnswer ?? item.answer ?? item.response ?? item.value ?? '')
          : item;

      const correctValue =
        item && typeof item === 'object'
          ? (item.correctAnswer ?? item.modelAnswer ?? item.expectedAnswer ?? '')
          : '';

      const correctness =
        item && typeof item === 'object'
          ? Boolean(item.isCorrect ?? item.correct ?? false)
          : false;

      return {
        questionId: toSafeDisplayValue(questionId),
        studentAnswer: toSafeDisplayValue(answerValue, ''),
        correctAnswer: toSafeDisplayValue(correctValue, ''),
        isCorrect: correctness
      };
    });
  }

  if (answers && typeof answers === 'object' && !Array.isArray(answers)) {
    return Object.entries(answers).map(([questionId, item]) => {
      const answerValue =
        item && typeof item === 'object'
          ? (item.studentAnswer ?? item.answer ?? item.response ?? item.value ?? '')
          : item;

      const correctValue =
        item && typeof item === 'object'
          ? (item.correctAnswer ?? item.modelAnswer ?? item.expectedAnswer ?? '')
          : '';

      const correctness =
        item && typeof item === 'object'
          ? Boolean(item.isCorrect ?? item.correct ?? false)
          : false;

      return {
        questionId: toSafeDisplayValue(questionId),
        studentAnswer: toSafeDisplayValue(answerValue, ''),
        correctAnswer: toSafeDisplayValue(correctValue, ''),
        isCorrect: correctness
      };
    });
  }

  return [];
};

/**
 * Branded Dynamic PDF Generator linked to Controlled Result Lifecycle
 */
export const generateTeacherReportPDF = (submission, diagnostics) => {
  if (submission && diagnostics) {
    ReportGenerator.generateStudentPDF(submission, diagnostics);
    return;
  }

  const classData = submission;
  const doc = new jsPDF();
  const dateGenerated = new Date().toLocaleDateString();

  const renderLogo = () => {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("LLAP | ACADEMY", 14, 20);
    doc.setFont("helvetica", "normal");
  };

  renderLogo();
  
  doc.setFontSize(12);
  doc.text("Language Learning Academy Platform", 14, 30);
  doc.setFontSize(10);
  doc.text("Professional IELTS Preparation System", 14, 36);
  doc.text("Learn • Communicate • Succeed", 14, 42);
  
  doc.setFontSize(10);
  doc.text(`Teacher: Authorized Supervisor`, 150, 20);
  doc.text(`Date Generated: ${dateGenerated}`, 150, 26);
  doc.line(14, 48, 196, 48);

  doc.setFontSize(16);
  doc.text("Teacher Analytics Report", 14, 60);
  autoTable(doc, {
    startY: 65,
    head: [['Metric', 'Value']],
    body: [
      ['Class Average Band', toSafeDisplayValue(classData?.averageBand, '6.5')],
      ['Completion Rate', `${toSafeDisplayValue(classData?.completionRate, '85')}%`],
      ['Top Weakness', toSafeDisplayValue(classData?.topWeakness, 'General')]
    ],
    theme: 'striped'
  });

  if (classData?.students) {
    doc.addPage();
    doc.text("Student Performance Analysis", 14, 20);
    autoTable(doc, {
      startY: 25,
      head: [['Student', 'Band', 'Progression']],
      body: classData.students.map(s => [toSafeDisplayValue(s.name), toSafeDisplayValue(s.band), `${toSafeDisplayValue(s.progression)}%`]),
      didParseCell: (data) => {
        if (data.column.index === 1 && data.cell.section === 'body') {
          const band = parseFloat(data.cell.raw);
          if (band < 5.0) data.cell.styles.textColor = [255, 0, 0];
          else if (band < 6.5) data.cell.styles.textColor = [204, 204, 0];
          else data.cell.styles.textColor = [0, 128, 0];
        }
      }
    });
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text("Confidential Academic Report | Page " + i + " of " + pageCount, 14, 290);
  }

  doc.save(`LLAP_TeacherAnalytics_Report_${dateGenerated.replace(/\//g, '-')}.pdf`);
};

const TeacherDashboardPage = () => {
  const { user, accessToken, logout } = useAuth();
  const [classData, setClassData] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  
  const [examFilter] = useState('ALL');
  
  // Controlled Lifecycle States
  const [selectedSubId, setSelectedSubId] = useState('');
  const [teacherComment, setTeacherComment] = useState('');
  const [isOverride, setIsOverride] = useState(false);
  const [overrideScore, setOverrideScore] = useState('');
  const [overrideBand, setOverrideBand] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [isReleasing, setIsReleasing] = useState(false);

  // Explicit Lifecycle management states
  const [reviewSnapshot, setReviewSnapshot] = useState(null);
  const [reviewedPdfGenerated, setReviewedPdfGenerated] = useState(false);
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const [releasedResult, setReleasedResult] = useState(null);
  const [lifecycleError, setLifecycleError] = useState('');

  const [manualCorrectness, setManualCorrectness] = useState({});

  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const selectorRef = useRef(null);

  const isInteractingRef = useRef(false);
  const requestIdRef = useRef(0);
  const [authFailed, setAuthFailed] = useState(false);

  const [newAssignment, setNewAssignment] = useState({ title: '', module: 'Reading', dueDate: '', targetStudentType: 'INSTRUCTOR' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  // Bind authFailed state directly to physical unmount/redirect lifecycle
  useEffect(() => {
    if (authFailed || !accessToken) {
      try {
        logout();
      } catch (err) {
        localStorage.removeItem('llap_access_token');
        localStorage.removeItem('llap_auth_token');
      }
      navigate('/login', { replace: true });
    }
  }, [authFailed, accessToken, logout, navigate]);

  useEffect(() => {
    if (accessToken) {
      setAuthFailed(false);
    }
  }, [accessToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setIsSelectorOpen(false);
        isInteractingRef.current = false;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNormalizedResults = useCallback((sub) => {
    return normalizeQuestionResults(sub);
  }, []);

  const fetchTeacherPortalData = useCallback(async (isBackgroundPoll = false) => {
    // Conditional lifecycle check: ensure valid accessToken exists before dispatching fetches
    if (!accessToken || authFailed || document.visibilityState === 'hidden') return;

    const currentRequestId = ++requestIdRef.current;
    if (isBackgroundPoll && (isInteractingRef.current || isSelectorOpen)) {
      return;
    }

    if (!isBackgroundPoll) {
      setLoading(true);
    }

    try {
      // Defensively parse API payloads using Promise.all with robust catch handling for local development HTML fallback routing
      const [dashRes, , subRes] = await Promise.all([
        TeacherService.getTeacherDashboard(accessToken).catch(err => ({ success: false, fallback: true, error: err })),
        AssignmentService.getAssignments(accessToken).catch(err => ({ success: true, assignments: JSON.parse(localStorage.getItem('llap_teacher_assignments') || '[]') })),
        GradingService.getSubmissions(accessToken).catch(err => ({ success: true, submissions: JSON.parse(localStorage.getItem('llap_teacher_submissions') || '[]') }))
      ]);

      if (currentRequestId !== requestIdRef.current) return;

      if (dashRes && dashRes.success) {
        const analyticsPayload = dashRes.dashboard || dashRes.analytics || AssessmentService.getClassAnalytics();
        setClassData(analyticsPayload);
      } else {
        setClassData(AssessmentService.getClassAnalytics());
      }

      const remoteSubs = (subRes && subRes.success && Array.isArray(subRes.submissions)) 
        ? [...subRes.submissions] 
        : [...JSON.parse(localStorage.getItem('llap_teacher_submissions') || '[]')];
      
      // Exclude instructor student automated direct releases from blocking manual review queues (filtering out direct-released / instructor-student bypass records)
      const filteredQueueSubs = remoteSubs.filter(sub => 
        sub.reviewStatus !== 'DIRECT_INSTRUCTOR_STUDENT_RELEASE' && 
        sub.directRelease !== true && 
        sub.studentClassification !== 'INSTRUCTOR' &&
        sub.studentClassification !== 'INSTRUCTOR_STUDENT'
      );

      filteredQueueSubs.sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
      setSubmissions(filteredQueueSubs);

      if (!selectedSubId && filteredQueueSubs.length > 0) {
        const pending = filteredQueueSubs.find(s => s.status !== 'Released') || filteredQueueSubs[0];
        if (pending) setSelectedSubId(pending.submissionId || pending._id);
      }

    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      if (err.status === 401 || err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setAuthFailed(true);
      } else {
        console.warn('Portal telemetry warning (using local fallbacks):', err.message);
      }
      if (!classData) setClassData(AssessmentService.getClassAnalytics());
      
      const localSubs = JSON.parse(localStorage.getItem('llap_teacher_submissions') || '[]');
      const filteredLocalSubs = localSubs.filter(sub => 
        sub.reviewStatus !== 'DIRECT_INSTRUCTOR_STUDENT_RELEASE' && 
        sub.directRelease !== true && 
        sub.studentClassification !== 'INSTRUCTOR' &&
        sub.studentClassification !== 'INSTRUCTOR_STUDENT'
      );
      setSubmissions(filteredLocalSubs);
    } finally {
      if (!isBackgroundPoll && currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [accessToken, authFailed, selectedSubId, classData, isSelectorOpen]);

  useEffect(() => {
    // Conditional lifecycle check: only poll if accessToken is verified and auth hasn't failed
    if (!accessToken || authFailed) return;
    fetchTeacherPortalData(false);

    const handleVisibilityChange = () => {
      if (accessToken && document.visibilityState === 'visible' && !authFailed) {
        fetchTeacherPortalData(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const pollInterval = setInterval(() => {
      if (accessToken && !authFailed && document.visibilityState === 'visible') {
        fetchTeacherPortalData(true);
      }
    }, 15000);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accessToken, authFailed, fetchTeacherPortalData]);

  const filteredSubmissions = submissions.filter(sub => {
    if (examFilter === 'ALL') return true;
    
    const examIdNorm = (sub.examId || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const assessNameNorm = (sub.assessmentName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const combinedStr = `${examIdNorm}_${assessNameNorm}`;

    if (examFilter === 'MOCK_1') {
      return combinedStr.includes('r1') || combinedStr.includes('reading1') || combinedStr.includes('mock1');
    }
    if (examFilter === 'MOCK_2') {
      return combinedStr.includes('r2') || combinedStr.includes('reading2') || combinedStr.includes('mock2');
    }
    return true;
  });

  const activeSubmission = submissions.find(s => (s.submissionId || s._id) === selectedSubId);

  useEffect(() => {
    if (!activeSubmission) {
      setManualCorrectness({});
      return;
    }
    const normalized = getNormalizedResults(activeSubmission);
    const initialMap = {};
    normalized.forEach((q) => {
      initialMap[q.questionId] = q.isCorrect;
    });
    setManualCorrectness(initialMap);
  }, [selectedSubId, activeSubmission, getNormalizedResults]);

  const activeDiagnostics = useMemo(() => {
    if (!activeSubmission) return null;

    const normalized = getNormalizedResults(activeSubmission);

    const reconciledResults = normalized.map(q => {
      const isCorr =
        manualCorrectness[q.questionId] !== undefined
          ? Boolean(manualCorrectness[q.questionId])
          : Boolean(q.isCorrect);

      return {
        ...q,
        questionId: toSafeDisplayValue(q.questionId),
        studentAnswer: toSafeDisplayValue(q.studentAnswer, ''),
        correctAnswer: toSafeDisplayValue(q.correctAnswer, ''),
        isCorrect: isCorr
      };
    });

    const correctCount = reconciledResults.filter(q => q.isCorrect).length;
    const totalQuestions = reconciledResults.length || Number(activeSubmission.totalQuestions) || 40;

    const clonedSubmission = {
      ...safeClone(activeSubmission),
      results: reconciledResults,
      rawScore: correctCount,
      totalQuestions
    };

    return DiagnosticEngine.analyze(clonedSubmission);
  }, [activeSubmission, manualCorrectness, getNormalizedResults]);

  const buildTeacherReviewedResult = useCallback(() => {
    if (!activeSubmission) return null;

    const normalized = getNormalizedResults(activeSubmission);

    const reviewedResults = normalized.map(q => {
      const reviewedCorrectness =
        manualCorrectness[q.questionId] !== undefined
          ? Boolean(manualCorrectness[q.questionId])
          : Boolean(q.isCorrect);

      return {
        questionId: toSafeDisplayValue(q.questionId),
        studentAnswer: toSafeDisplayValue(q.studentAnswer, ''),
        correctAnswer: toSafeDisplayValue(q.correctAnswer, ''),
        isCorrect: reviewedCorrectness
      };
    });

    const correctCount = reviewedResults.filter(q => q.isCorrect).length;
    const totalQuestions = reviewedResults.length || Number(activeSubmission.totalQuestions) || 40;

    const rawScore = isOverride
      ? Number.parseInt(overrideScore, 10)
      : correctCount;

    const calculatedBand = GradingEngine.convertToIELTSBand(
      isOverride ? rawScore : correctCount
    );

    const estimatedBand = isOverride
      ? toSafeDisplayValue(overrideBand, `Band ${calculatedBand}`)
      : `Band ${calculatedBand}`;

    return {
      ...safeClone(activeSubmission),
      submissionId: activeSubmission.submissionId || activeSubmission._id,
      results: reviewedResults,
      rawScore: Number.isFinite(rawScore) ? rawScore : correctCount,
      totalQuestions,
      estimatedBand,
      teacherFeedback: toSafeDisplayValue(teacherComment, ''),
      overrideReason: isOverride ? toSafeDisplayValue(overrideReason, '') : '',
      reviewStatus: RESULT_STATUS.TEACHER_REVIEWED,
      lifecycleStatus: RESULT_STATUS.TEACHER_REVIEWED,
      studentVisibility: RESULT_VISIBILITY.HIDDEN,
      teacherReviewedAt: new Date().toISOString(),
      originalResultId: activeSubmission.originalResultId || activeSubmission.submissionId || activeSubmission._id
    };
  }, [
    activeSubmission,
    getNormalizedResults,
    manualCorrectness,
    isOverride,
    overrideScore,
    overrideBand,
    overrideReason,
    teacherComment
  ]);

  const handleGenerateReviewedPDF = useCallback(async () => {
    if (!activeSubmission) {
      alert('Please select a submission first.');
      return null;
    }

    setLifecycleError('');

    try {
      const reviewedResult = buildTeacherReviewedResult();

      if (!reviewedResult) {
        throw new Error('Unable to create teacher-reviewed result.');
      }

      const diagnostics = DiagnosticEngine.analyze(reviewedResult);

      ReportGenerator.generateStudentPDF(reviewedResult, diagnostics);

      const snapshot = {
        ...reviewedResult,
        reviewStatus: RESULT_STATUS.REVIEWED_PDF,
        lifecycleStatus: RESULT_STATUS.REVIEWED_PDF,
        reviewedPdfGeneratedAt: new Date().toISOString(),
        studentVisibility: RESULT_VISIBILITY.HIDDEN
      };

      setReviewSnapshot(snapshot);
      setReviewedPdfGenerated(true);

      return { snapshot, diagnostics };
    } catch (error) {
      console.error('Reviewed PDF generation failed:', error);
      setLifecycleError(error.message || 'Unable to generate reviewed PDF.');
      alert(error.message || 'Unable to generate reviewed PDF.');
      return null;
    }
  }, [activeSubmission, buildTeacherReviewedResult]);

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!accessToken) return;
    try {
      const assignmentPayload = {
        ...newAssignment,
        targetStudentType: newAssignment.targetStudentType || 'INSTRUCTOR'
      };

      const res = await AssignmentService.createAssignment(accessToken, assignmentPayload);
      if (res && res.success) {
        alert('Assignment scheduled successfully!');
        setNewAssignment({ title: '', module: 'Reading', dueDate: '', targetStudentType: 'INSTRUCTOR' });
        
        const assignRes = await AssignmentService.getAssignments(accessToken);
        if (assignRes && assignRes.success) {
          // assignments updated
        }
        setActiveTab('assignments');
      } else {
        alert('Failed to schedule assignment.');
      }
    } catch (err) {
      alert(`Error scheduling assignment: ${err.message}`);
    }
  };

  const handleApproveAndRelease = async e => {
    e.preventDefault();

    if (!accessToken || !selectedSubId || !activeSubmission || isReleasing) {
      return;
    }

    setLifecycleError('');

    let lifecycleSnapshot = reviewSnapshot;

    if (
      !lifecycleSnapshot ||
      !reviewedPdfGenerated ||
      lifecycleSnapshot.submissionId !== selectedSubId
    ) {
      const generated = await handleGenerateReviewedPDF();
      if (!generated) {
        return;
      }
      lifecycleSnapshot = generated.snapshot;
    }

    const confirmed = window.confirm(
      'I confirm that I have reviewed the student answers, score, teacher feedback, and Reviewed PDF. I approve this result for release to the student.'
    );

    if (!confirmed) {
      return;
    }

    setApprovalConfirmed(true);
    setIsReleasing(true);

    try {
      const approvedAt = new Date().toISOString();

      const approvedSnapshot = {
        ...safeClone(lifecycleSnapshot),
        reviewStatus: RESULT_STATUS.APPROVED,
        lifecycleStatus: RESULT_STATUS.APPROVED,
        teacherApproved: true,
        approvedAt,
        studentVisibility: RESULT_VISIBILITY.HIDDEN
      };

      const gradingPayload = {
        submissionId: selectedSubId,
        rawScore: approvedSnapshot.rawScore,
        estimatedBand: approvedSnapshot.estimatedBand,
        teacherFeedback: approvedSnapshot.teacherFeedback,
        overrideReason: approvedSnapshot.overrideReason,
        results: approvedSnapshot.results,
        reviewStatus: RESULT_STATUS.APPROVED,
        lifecycleStatus: RESULT_STATUS.APPROVED,
        teacherApproved: true,
        approvedAt,
        releaseResult: false,
        studentVisibility: RESULT_VISIBILITY.HIDDEN
      };

      const approvalResponse = await GradingService.gradeSubmission(accessToken, gradingPayload);

      if (!approvalResponse || !approvalResponse.success) {
        throw new Error(approvalResponse?.error || 'Teacher approval could not be saved.');
      }

      const releasedSnapshot = {
        ...safeClone(approvedSnapshot),
        reviewStatus: RESULT_STATUS.RELEASED,
        lifecycleStatus: RESULT_STATUS.RELEASED,
        status: 'Released',
        releasedAt: new Date().toISOString(),
        studentVisibility: RESULT_VISIBILITY.STUDENT
      };

      const finalDiagnostics = DiagnosticEngine.analyze(releasedSnapshot);

      ReportGenerator.generateStudentPDF(releasedSnapshot, finalDiagnostics);

      const releasePayload = {
        submissionId: selectedSubId,
        rawScore: releasedSnapshot.rawScore,
        estimatedBand: releasedSnapshot.estimatedBand,
        teacherFeedback: releasedSnapshot.teacherFeedback,
        results: releasedSnapshot.results,
        reviewStatus: RESULT_STATUS.RELEASED,
        lifecycleStatus: RESULT_STATUS.RELEASED,
        teacherApproved: true,
        approvedAt: releasedSnapshot.approvedAt,
        releasedAt: releasedSnapshot.releasedAt,
        releaseResult: true,
        studentVisibility: RESULT_VISIBILITY.STUDENT
      };

      const releaseResponse = await GradingService.gradeSubmission(accessToken, releasePayload);

      if (!releaseResponse || !releaseResponse.success) {
        throw new Error(releaseResponse?.error || 'Released result could not be persisted.');
      }

      const localStorageKey = 'llap_teacher_submissions';
      const localSubs = JSON.parse(localStorage.getItem(localStorageKey) || '[]');

      const updatedSubs = localSubs.map(sub => {
        const id = sub.submissionId || sub._id;
        if (id !== selectedSubId) {
          return sub;
        }

        return {
          ...safeClone(sub),
          submissionId: releasedSnapshot.submissionId,
          results: releasedSnapshot.results,
          rawScore: releasedSnapshot.rawScore,
          totalQuestions: releasedSnapshot.totalQuestions,
          estimatedBand: releasedSnapshot.estimatedBand,
          teacherFeedback: releasedSnapshot.teacherFeedback,
          reviewStatus: RESULT_STATUS.RELEASED,
          lifecycleStatus: RESULT_STATUS.RELEASED,
          status: 'Released',
          teacherApproved: true,
          approvedAt: releasedSnapshot.approvedAt,
          releasedAt: releasedSnapshot.releasedAt,
          studentVisibility: RESULT_VISIBILITY.STUDENT
        };
      });

      localStorage.setItem(localStorageKey, JSON.stringify(updatedSubs));
      setReleasedResult(releasedSnapshot);

      await fetchTeacherPortalData(false);

      alert('Result successfully reviewed, PDF generated, approved, released, and made visible to the student.');

      setSelectedSubId('');
      setTeacherComment('');
      setIsOverride(false);
      setOverrideScore('');
      setOverrideBand('');
      setOverrideReason('');
      setManualCorrectness({});
      setReviewSnapshot(null);
      setReviewedPdfGenerated(false);
      setApprovalConfirmed(false);
      isInteractingRef.current = false;
    } catch (error) {
      console.error('Controlled result release failed:', error);
      setLifecycleError(error.message || 'Controlled result release failed.');
      alert(error.message || 'Controlled result release failed. The student result remains hidden.');
    } finally {
      setIsReleasing(false);
    }
  };

  const exportExcel = () => {
    if (!activeSubmission || !activeDiagnostics) {
      alert('Please select a student submission to export.');
      return;
    }
    const summaryData = [
      { Metric: "Candidate Name", Value: toSafeDisplayValue(activeSubmission.studentName) },
      { Metric: "Assessment", Value: toSafeDisplayValue(activeSubmission.assessmentName) },
      { Metric: "Raw Score", Value: `${activeDiagnostics.correctCount} / ${activeDiagnostics.totalQuestions}` },
      { Metric: "Percentage", Value: `${activeDiagnostics.percentage}%` },
      { Metric: "Estimated Band", Value: toSafeDisplayValue(activeSubmission.estimatedBand, `Band ${GradingEngine.convertToIELTSBand(activeDiagnostics.correctCount)}`) },
      { Metric: "Status", Value: toSafeDisplayValue(activeSubmission.status, 'Pending Review') }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), "Result Summary");
    XLSX.writeFile(workbook, `LLAP_Student_Report_${toSafeDisplayValue(activeSubmission.studentName, 'Candidate')}.xlsx`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Conditional lifecycle check for loading state when credentials are unverified
  if (!accessToken || (loading && !classData && !authFailed)) {
    return <div style={{ padding: '60px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}><h2>Verifying Credentials & Loading Teacher Portal...</h2></div>;
  }

  const safeClassData = classData || AssessmentService.getClassAnalytics();
  const normalizedActiveResults = activeSubmission ? getNormalizedResults(activeSubmission) : [];

  return (
    <div style={styles.container}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #eaeaea', paddingBottom: '20px', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#0056b3', margin: '0 0 5px 0' }}>LLAP Academy — Teacher Portal & Assignment Manager</h1>
          <p style={{ color: '#666', margin: 0 }}>Instructor: {toSafeDisplayValue(user?.fullName, 'Authorized Supervisor')} (Authorized Supervisor)</p>
        </div>
        <button onClick={handleLogout} style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
          Logout
        </button>
      </header>

      {authFailed && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '6px', border: '1px solid #f5c6cb', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><strong>Notice:</strong> Server synchronization halted due to session expiration (401). Redirecting to login...</span>
          <button onClick={() => navigate('/login')} style={{ padding: '6px 12px', background: '#721c24', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Re-authenticate</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '15px', marginBottom: '30px', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>
        <button onClick={() => setActiveTab('overview')} style={{ padding: '10px 18px', background: activeTab === 'overview' ? '#0056b3' : '#fff', color: activeTab === 'overview' ? '#fff' : '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Analytics & Overview</button>
        <button onClick={() => setActiveTab('assignments')} style={{ padding: '10px 18px', background: activeTab === 'assignments' ? '#0056b3' : '#fff', color: activeTab === 'assignments' ? '#fff' : '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Test Assignment Scheduling</button>
        <button onClick={() => setActiveTab('grading')} style={{ padding: '10px 18px', background: activeTab === 'grading' ? '#0056b3' : '#fff', color: activeTab === 'grading' ? '#fff' : '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Student Grading & Result Release</button>
      </div>

      <div style={styles.actions}>
        <button onClick={() => generateTeacherReportPDF(activeSubmission, activeDiagnostics)} style={styles.btn}>Export Active Student PDF Report</button>
        <button onClick={exportExcel} style={{ ...styles.btn, backgroundColor: '#28a745' }}>Export Active Student Excel</button>
      </div>

      {workspace ? (
        <div style={{ background: '#f9f9f9', padding: '25px', borderRadius: '8px', border: '1px solid #0056b3', marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, color: '#0056b3' }}>Student Workspace: {toSafeDisplayValue(workspace.profile.fullName)}</h2>
            <button onClick={() => setWorkspace(null)} style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>Back to Overview</button>
          </div>
          <p><strong>Email:</strong> {toSafeDisplayValue(workspace.profile.email)}</p>
          <p><strong>Programme:</strong> {toSafeDisplayValue(workspace.profile.programme)}</p>
          <p><strong>Enrollment Status:</strong> {toSafeDisplayValue(workspace.profile.enrollmentStatus)}</p>
        </div>
      ) : activeTab === 'assignments' ? (
        <div style={{ background: '#fff', padding: '25px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '40px' }}>
          <h2>Reading & Module Test Assignment Scheduling</h2>
          <form onSubmit={handleCreateAssignment} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px', background: '#f9f9f9', padding: '20px', borderRadius: '6px', border: '1px solid #eee' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Assignment Title</label>
              <input type="text" value={newAssignment.title} onChange={e => setNewAssignment({...newAssignment, title: e.target.value})} placeholder="e.g. IELTS Reading Test" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Module</label>
              <select value={newAssignment.module} onChange={e => setNewAssignment({...newAssignment, module: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                <option value="Reading">Reading</option>
                <option value="Writing">Writing</option>
                <option value="Listening">Listening</option>
                <option value="Speaking">Speaking</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Due Date</label>
              <input type="datetime-local" value={newAssignment.dueDate} onChange={e => setNewAssignment({...newAssignment, dueDate: e.target.value})} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" style={{ width: '100%', padding: '9px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Schedule Assignment</button>
            </div>
          </form>
        </div>
      ) : activeTab === 'grading' ? (
        <div style={{ background: '#fff', padding: '25px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '40px' }}>
          <h2>Controlled Result Review & Release Workflow</h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, minmax(120px, 1fr))',
              gap: '8px',
              marginBottom: '25px'
            }}>
            {[
              ['1', 'Original Result', true],
              ['2', 'Teacher-Reviewed Result', Boolean(reviewSnapshot || activeSubmission)],
              ['3', 'Reviewed PDF', reviewedPdfGenerated],
              ['4', 'Teacher Approval', approvalConfirmed],
              ['5', 'Released PDF', Boolean(releasedResult)],
              ['6', 'Student Visibility', Boolean(releasedResult?.studentVisibility === RESULT_VISIBILITY.STUDENT)]
            ].map(([number, label, complete]) => (
              <div
                key={number}
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  background: complete ? '#dcfce7' : '#f8fafc',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '12px' }}>STEP {number}</div>
                <div style={{ fontSize: '11px', marginTop: '4px' }}>{label}</div>
                <div style={{ fontSize: '10px', fontWeight: 'bold', marginTop: '5px', color: complete ? '#166534' : '#64748b' }}>
                  {complete ? '✓ COMPLETE' : 'PENDING'}
                </div>
              </div>
            ))}
          </div>

          {lifecycleError && (
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '12px', borderRadius: '6px', marginBottom: '15px' }}>
              <strong>Workflow Error:</strong> {toSafeDisplayValue(lifecycleError, 'Unknown workflow error')}
            </div>
          )}
          
          <div style={{ marginBottom: '25px', position: 'relative' }} ref={selectorRef}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Select Student Submission for Review</label>
            <div 
              onClick={() => {
                const newState = !isSelectorOpen;
                setIsSelectorOpen(newState);
                isInteractingRef.current = newState;
              }}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', background: '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box' }}
            >
              <span>
                {selectedSubId ? (() => {
                  const sub = submissions.find(s => (s.submissionId || s._id) === selectedSubId);
                  return sub 
                    ? `${toSafeDisplayValue(sub.studentName, 'Student')} — ${toSafeDisplayValue(sub.assessmentName, 'Mock Test')} (Score: ${toSafeDisplayValue(sub.rawScore, '0')}/40)`
                    : '-- Choose Submission --';
                })() : '-- Choose Submission --'}
              </span>
              <span>▼</span>
            </div>

            {isSelectorOpen && (
              <div 
                onMouseEnter={() => { isInteractingRef.current = true; }}
                onMouseLeave={() => { isInteractingRef.current = false; }}
                style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto', background: '#fff', border: '1px solid #0056b3', borderRadius: '4px', zIndex: 9999, marginTop: '4px' }}
              >
                <div onClick={() => { setSelectedSubId(''); setIsSelectorOpen(false); isInteractingRef.current = false; }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                  -- Choose Submission --
                </div>
                {filteredSubmissions.map((sub, i) => {
                  const subKey = sub.submissionId || sub._id || i;
                  return (
                    <div key={subKey} onClick={() => { setSelectedSubId(sub.submissionId || sub._id); setIsSelectorOpen(false); isInteractingRef.current = false; }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
                      {toSafeDisplayValue(sub.studentName, 'Student')} — {toSafeDisplayValue(sub.assessmentName, 'Mock Test')} (Score: {toSafeDisplayValue(sub.rawScore, '0')}/40)
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {activeSubmission && activeDiagnostics && (
            <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #0056b3', marginBottom: '25px' }}>
              <h3>Teacher-Reviewed Result Summary for: {toSafeDisplayValue(activeSubmission.studentName)}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '20px' }}>
                <div style={styles.metricCard}><p style={styles.mTitle}>RECONCILED SCORE</p><p style={styles.mValue}>{activeDiagnostics.correctCount} / {activeDiagnostics.totalQuestions}</p></div>
                <div style={styles.metricCard}><p style={styles.mTitle}>CORRECT ITEMS</p><p style={styles.mValue}>{activeDiagnostics.correctCount}</p></div>
                <div style={styles.metricCard}><p style={styles.mTitle}>INCORRECT ITEMS</p><p style={styles.mValue}>{activeDiagnostics.incorrectCount}</p></div>
                <div style={styles.metricCard}><p style={styles.mTitle}>CALCULATED BAND</p><p style={styles.mValue}>Band {GradingEngine.convertToIELTSBand(activeDiagnostics.correctCount)}</p></div>
              </div>

              {/* Itemized Answer Verification & Review Table — Fully Flattened & Sanitized */}
              <div style={{ background: '#fff', padding: '15px', borderRadius: '6px', border: '1px solid #ccc', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#0056b3' }}>Itemized Answer Review & Correction</h4>
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ padding: '8px', textAlign: 'left', width: '20%' }}>Question</th>
                        <th style={{ padding: '8px', textAlign: 'left', width: '40%' }}>Student Answer</th>
                        <th style={{ padding: '8px', textAlign: 'left', width: '20%' }}>Model Answer</th>
                        <th style={{ padding: '8px', textAlign: 'center', width: '20%' }}>Review Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {normalizedActiveResults.map((q, idx) => {
                        const qKey = q.questionId;
                        const isCorrect = manualCorrectness[qKey] !== undefined ? manualCorrectness[qKey] : q.isCorrect;

                        return (
                          <tr key={qKey || idx} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{toSafeDisplayValue(qKey)}</td>
                            <td style={{ padding: '8px', wordBreak: 'break-word' }}>
                              {toSafeDisplayValue(q.studentAnswer, '(Unanswered)') || '(Unanswered)'}
                            </td>
                            <td style={{ padding: '8px', color: '#047857', wordBreak: 'break-word' }}>
                              {toSafeDisplayValue(q.correctAnswer, 'N/A')}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  isInteractingRef.current = true;
                                  setManualCorrectness({ ...manualCorrectness, [qKey]: !isCorrect });
                                }}
                                style={{
                                  background: isCorrect ? '#dcfce7' : '#fee2e2',
                                  color: isCorrect ? '#166534' : '#991b1b',
                                  border: `1px solid ${isCorrect ? '#86efac' : '#fca5a5'}`,
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer'
                                }}
                              >
                                {isCorrect ? '✓ Correct' : '✕ Incorrect'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <form onSubmit={handleApproveAndRelease}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Professional Teacher Comment</label>
                  <textarea value={teacherComment} onChange={e => setTeacherComment(e.target.value)} placeholder="Add qualitative feedback..." rows="3" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={isReleasing || !activeSubmission}
                    onClick={handleGenerateReviewedPDF}
                    style={{
                      padding: '12px 24px',
                      background: '#0056b3',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Generate Reviewed PDF
                  </button>

                  <button 
                    type="submit" 
                    disabled={isReleasing} 
                    style={{ 
                      padding: '12px 24px', 
                      background: '#28a745', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '4px', 
                      fontWeight: 'bold', 
                      cursor: 'pointer' 
                    }}
                  >
                    {isReleasing ? 'Processing Controlled Release...' : 'Teacher Approval & Release Result'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      ) : (
        <div>
          <section style={styles.card}>
            <h2>Class Performance Overview</h2>
            <div style={styles.grid}>
              <div style={styles.metric}><h3>Class Average</h3><p>{toSafeDisplayValue(safeClassData.averageBand)}</p></div>
              <div style={styles.metric}><h3>Participation</h3><p>{toSafeDisplayValue(safeClassData.completionRate)}%</p></div>
            </div>
          </section>

          <TeachingPriorityPanel heatmapData={safeClassData.heatmapData} />
          <Heatmap data={safeClassData.heatmapData} />
          <PerformanceDistribution bandDistribution={safeClassData.bandDistribution} skillAverages={safeClassData.skillAverages} />
          <LearningObjectiveAnalytics data={safeClassData.objectiveAnalytics} />
          <CohortComparison data={safeClassData.cohortComparisonData} />
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1200px', margin: '0 auto' },
  card: { border: '1px solid #ccc', padding: '15px', borderRadius: '8px', marginBottom: '20px', backgroundColor: '#fff' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
  metric: { textAlign: 'center' },
  metricCard: { background: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #ccc', textAlign: 'center' },
  mTitle: { margin: '0 0 5px 0', fontSize: '10px', color: '#666', fontWeight: 'bold' },
  mValue: { margin: 0, fontSize: '16px', color: '#0056b3', fontWeight: 'bold' },
  actions: { marginBottom: '20px', display: 'flex', gap: '10px' },
  btn: { padding: '10px 20px', cursor: 'pointer', backgroundColor: '#2196f3', color: 'white', border: 'none', borderRadius: '4px' }
};

export default TeacherDashboardPage;